import { ArrowLeft, Dumbbell } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";

const ActivityRecoveryPage = () => {
  const navigate = useNavigate();

  // Weekly active recovery data (in minutes)
  const weeklyData = [
    { day: "Mon", minutes: 45 },
    { day: "Tue", minutes: 20 },
    { day: "Wed", minutes: 55 },
    { day: "Thu", minutes: 15 },
    { day: "Fri", minutes: 10 },
    { day: "Sat", minutes: 35 },
    { day: "Sun", minutes: 12 },
  ];

  const maxMinutes = 60;

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-8"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-foreground">
            Activity Recovery
          </h1>
        </div>

        {/* Today's Recovery Activity Card */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-gray-100 p-3 dark:bg-gray-800">
                <Dumbbell className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Today's Recovery Activity
                </p>
                <div className="mb-1 text-2xl font-bold text-foreground">
                  --
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  No data available
                </p>
              </div>
            </div>
            <span className="rounded-md bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              No Data
            </span>
          </div>
        </Card>

        {/* Stress Buffer Strength */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Stress Buffer Strength
          </h3>

          {/* Gradient Bar */}
          <div className="relative mb-1 h-2 overflow-hidden rounded-full bg-gradient-to-r from-blue-500 via-yellow-400 to-green-500">
            <div
              className="absolute top-0 h-full w-1 bg-gray-800 dark:bg-white"
              style={{ left: "0%" }}
            />
          </div>
          <div className="mb-2 flex justify-between text-[10px] text-gray-500 dark:text-gray-400">
            <span>Minimal</span>
            <span>Moderate</span>
            <span>Strong</span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Light movement can modestly buffer physiological load.
          </p>
        </Card>

        {/* Weekly Active Recovery Chart */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <h3 className="mb-4 text-sm font-semibold text-foreground">
            Weekly Active Recovery (min)
          </h3>

          {/* Chart */}
          <div className="relative h-48">
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 flex h-full flex-col justify-between text-[10px] text-gray-500 dark:text-gray-400">
              <span>60</span>
              <span>45</span>
              <span>30</span>
              <span>15</span>
              <span>0</span>
            </div>

            {/* Chart area */}
            <div className="ml-6 flex h-full items-end justify-around gap-1">
              {weeklyData.map((item, index) => {
                const heightPercent = (item.minutes / maxMinutes) * 100;
                return (
                  <div
                    key={index}
                    className="flex flex-1 flex-col items-center gap-1"
                  >
                    <div className="relative w-full" style={{ height: "160px" }}>
                      <div
                        className="absolute bottom-0 w-full rounded-t bg-green-500"
                        style={{ height: `${heightPercent}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      {item.day}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        {/* What This Means Section */}
        <Card className="rounded-xl bg-blue-50/80 p-4 shadow-sm dark:bg-blue-900/20">
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-blue-600 dark:border-blue-400">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                i
              </span>
            </div>
            <h3 className="text-sm font-semibold text-foreground">
              What This Means
            </h3>
          </div>

          <div className="space-y-3 text-xs text-gray-700 dark:text-gray-300">
            <p>
              <span className="font-semibold">Active recovery</span> refers to
              low-intensity movement like walking, stretching, or yoga that helps
              your body process stress hormones and restore balance.
            </p>

            <p>
              <span className="font-semibold">With low activity today,</span>{" "}
              your body has limited buffering against physiological stress. Even
              20-30 minutes of gentle movement can meaningfully reduce your
              stress index.
            </p>

            <p>
              <span className="font-semibold">
                The effect is modest but real:
              </span>{" "}
              active recovery improves circulation, promotes parasympathetic
              activation, and can lower cortisol levels within hours.
            </p>
          </div>
        </Card>

        {/* Quick Recovery Activities Section */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Quick Recovery Activities
          </h3>
          <ul className="space-y-2 text-xs text-gray-700 dark:text-gray-300">
            <li className="flex gap-2">
              <span className="text-green-500">•</span>
              <span>Take a 20-minute walk after lunch</span>
            </li>
            <li className="flex gap-2">
              <span className="text-green-500">•</span>
              <span>Do 10 minutes of gentle stretching</span>
            </li>
            <li className="flex gap-2">
              <span className="text-green-500">•</span>
              <span>Try a short yoga flow (15 min)</span>
            </li>
            <li className="flex gap-2">
              <span className="text-green-500">•</span>
              <span>Use a foam roller for 5-10 minutes</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default ActivityRecoveryPage;
